import React, { useEffect } from 'react';
import * as AnalyseAction from '../../redux/actions/analyseAction';
import { launchCamera, launchImageLibrary } from 'react-native-image-picker';
import uuid from 'react-native-uuid';
import { useSelector, useDispatch } from "react-redux";
import * as AppActions from '../../redux/actions/appAction';
import { MANUAL_UPLOAD_TIMEOUT, SCAN_TIMEOUT, UPLOAD_TIMEOUT, DOUBLE_SIDE_SCAN_TIMEOUT } from '../../constants';
import { IStore } from '../../redux/reducers';
import { IJobAdditionalAttributes, IManualJobEntryInput, JobStatus, isImageScanningOrUploading, isScanError } from '../../models';
import MeasurementInfo from 'src/components/MeasurementInfo';
import InfoStatusBar from 'src/components/InfoStatusBar';
import { Auth as Authorization } from 'aws-amplify';
import { Box, Button, HStack, Image, Modal, Text, VStack, useColorModeValue } from 'native-base';
import CustomAlertDialog from 'src/components/CustomAlertDialog';
import ProgressBar from 'src/components/ProgressBar';
import FabButtons from 'src/components/FabButtons';
import { containerStyle } from 'src/styles/containerStyle';
import { getCurrentLocation } from 'src/utils/location';
import { useWindowDimensions, Platform, ScrollView } from 'react-native';
import windowsScan from '../../services/WindowsScan';
import { isDoubleSidedScanner } from '../../models/UserSettings';
import { shouldUseLocalProcessing, isElectron } from 'src/utils/miscUtils';
import * as JobActions from '../../redux/actions/jobAction';
import { useIsFocused } from '@react-navigation/native';
import { getDisplayableJobId } from 'src/utils/jobUtils';
import { getText } from 'src/utils/i18n';
import { pickWebImages } from 'src/utils/webImagePicker';
import { isLightboxFeatureEnabled } from 'src/utils/grainFeatureUtils';

export default function InfoPage({ navigation }: { navigation: any }) {

    // Get react native screen dimensions
    const { width, height } = useWindowDimensions();

    const hideDialog = () => setDialogVisible(false);

    const [dialogVisible, setDialogVisible] = React.useState(false);
    const [dialogStatusMessage, setDialogStatusMessage] = React.useState("");
    const [dialogTitle, setDialogTitle] = React.useState("Error");
    const [dialogConfirmText, setDialogConfirmText] = React.useState("OK");
    const [dialogAction, setDialogAction] = React.useState<(() => void) | null>(null);

    const [waiting, setWaiting] = React.useState(false);

    const jobStatus = useSelector((store: IStore) => store.job.currentJobStatus);
    const createJobError = useSelector((store: IStore) => store.job.createJob.error);
    const userProfile = useSelector((store: IStore) => store.user.userProfile.response);
    const preferredProcessingMode = useSelector((store: IStore) => store.settings.preferredProcessingMode);
    const isOnline = useSelector((store: IStore) => store.app.isOnline);
    const lightboxEnabled = useSelector((store: IStore) =>
        isLightboxFeatureEnabled(store.user.userProfile.response?.settings)
    );
    const draftJobId = useSelector((store: IStore) => store.job.draftJobId);
    const draftInfo = useSelector((store: IStore) => store.job.draftInfo);
    const draftLocked = useSelector((store: IStore) => store.job.draftLocked);

    const dispatch = useDispatch();
    const isFocused = useIsFocused();
    const sampleIdBarBg = useColorModeValue('coolGray.100', 'coolGray.800');
    const sampleIdBarText = useColorModeValue('coolGray.800', 'coolGray.100');

    useEffect(() => {
        if (!isFocused) return;
        if (!draftJobId) {
            dispatch(JobActions.setDraftJobId(uuid.v4().toString()));
        }
    }, [isFocused, draftJobId, dispatch]);

    interface ImageAsset {
        uri: string;
        [key: string]: any;
    }

    const [selectedImageData, setSelectedImageData] = React.useState<{
        assets: ImageAsset[];
        source: string;
        doubleSided: boolean;
        jobOptions?: {
            productName?: string;
            additionalAttributes?: IJobAdditionalAttributes;
        };
    }>({
        assets: [],
        source: "",
        doubleSided: false,
    });

    const [showPreview, setShowPreview] = React.useState(false);

    const effectiveOnline =
        isOnline !== false
        && (typeof navigator === 'undefined' || typeof navigator.onLine !== 'boolean' || navigator.onLine !== false);

    const useLocalProcessing = shouldUseLocalProcessing(preferredProcessingMode, effectiveOnline);

    const showOfflineDialog = (message?: string) => {
        setDialogTitle('Offline');
        setDialogStatusMessage(message || 'You are offline. Connect to the internet to use cloud features.');
        setDialogConfirmText('OK');
        setDialogAction(null);
        setDialogVisible(true);
    };

    useEffect(() => {
        if (isScanError(jobStatus)) {
            displayScanError(jobStatus);
        }
        // Clear the local scanner/upload indicator as soon as the operation has
        // completed. JOB_IMAGE_SCANNED is the terminal state for the scanner;
        // subsequent upload/processing is handled in the background.
        if (jobStatus === JobStatus.JOB_IMAGE_SCANNED
            || jobStatus === JobStatus.JOB_IMAGE_UPLOADED
            || jobStatus === JobStatus.JOB_PROCESSING_LOCAL
            || jobStatus === JobStatus.JOB_IN_PROGRESS
            || jobStatus === JobStatus.JOB_COMPLETED
            || jobStatus === JobStatus.JOB_FAILED
            || isScanError(jobStatus)) {
            setWaiting(false);
        }
    }, [jobStatus]);

    // Helper function to extract clean error message from server response
    const extractErrorMessage = (errorMessage: string): string => {
        // Remove "FetchError: Error fetching." prefix
        let cleanMessage = errorMessage.replace(/^FetchError:\s*Error fetching\.\s*"?/i, '');
        // Remove trailing quote if present
        cleanMessage = cleanMessage.replace(/"$/, '');
        return cleanMessage.trim();
    };

    // Helper function to parse and handle different error types
    const handleJobCreationError = (error: Error) => {
        const rawErrorMessage = error.message || '';
        const errorMessage = extractErrorMessage(rawErrorMessage);

        // Check for usage limit exceeded error
        if (errorMessage.includes('Usage validation failed') && errorMessage.includes('limit exceeded')) {
            setDialogTitle('Subscription Limit Reached');
            setDialogStatusMessage(
                'You have reached your monthly usage limit.\n\nUpgrade your subscription to continue analyzing grain samples.'
            );
            setDialogConfirmText('Upgrade Now');
            setDialogAction(() => () => {
                navigation.navigate('ManageUserSubscription');
            });
        }
        // Add more error types here in the future
        // Example: Network errors, authentication errors, etc.
        else {
            setDialogTitle('Error');
            setDialogStatusMessage(errorMessage || 'Failed to create job. Please try again.');
            setDialogConfirmText('OK');
            setDialogAction(null);
        }

        setWaiting(false);
        setDialogVisible(true);
    };

    useEffect(() => {
        if (createJobError) {
            handleJobCreationError(createJobError);
        }
    }, [createJobError]);

    const checkAuthAndManualUpload = async (payload: IManualJobEntryInput) => {
        // In Electron local/offline mode, do not require an online auth check.
        if (isElectron() && useLocalProcessing) {
            await processManualJob(payload);
            return;
        }
        Authorization.currentUserInfo()
            .then(async () => {
                await processManualJob(payload)
            })
            .catch((e) => {
                console.log('error occurred', e)
                if (effectiveOnline) {
                    if (isElectron()) {
                        dispatch(AppActions.setCloudAuthStatus({
                            status: 'invalid',
                            reason: 'auth_check_failed',
                            updatedAt: Date.now(),
                        }));
                    } else {
                        dispatch(AppActions.forceLogout());
                    }
                } else {
                    showOfflineDialog();
                }
            });
    }

    const checkAuthAndUpload = async (
        data: any,
        source = "image",
        jobOptions?: {
            productName?: string;
            additionalAttributes?: IJobAdditionalAttributes;
        }
    ) => {
        // In Electron local/offline mode, do not require an online auth check.
        if (isElectron() && useLocalProcessing) {
            setSelectedImageData({
                assets: data?.assets,
                source: source,
                doubleSided: false,
                jobOptions
            });
            setShowPreview(true);
            return;
        }
        Authorization.currentUserInfo()
            .then(async () => {
                setSelectedImageData({
                    assets: data?.assets,
                    source: source,
                    doubleSided: false,
                    jobOptions
                });
                setShowPreview(true);
            })
            .catch((e) => {
                console.log('error occurred', e)
                if (effectiveOnline) {
                    if (isElectron()) {
                        dispatch(AppActions.setCloudAuthStatus({
                            status: 'invalid',
                            reason: 'auth_check_failed',
                            updatedAt: Date.now(),
                        }));
                    } else {
                        dispatch(AppActions.forceLogout());
                    }
                } else {
                    showOfflineDialog();
                }
            });
    }

    const checkAuthAndDoubleSidedUpload = async (data: any, source = "image") => {
        const assetCount = data?.assets?.length ?? 0;
        if (assetCount !== 2) {
            setDialogTitle('Image Selection');
            setDialogConfirmText('OK');
            setDialogStatusMessage('Please select exactly 2 images (front and back).');
            setDialogAction(null);
            setDialogVisible(true);
            return;
        }

        // In Electron local/offline mode, do not require an online auth check.
        if (isElectron() && useLocalProcessing) {
            setSelectedImageData({
                assets: data?.assets,
                source: source,
                doubleSided: true,
            });
            setShowPreview(true);
            return;
        }
        Authorization.currentUserInfo()
            .then(async () => {
                setSelectedImageData({
                    assets: data?.assets,
                    source: source,
                    doubleSided: true,
                });
                setShowPreview(true);
            })
            .catch((e) => {
                console.log('error occurred', e)
                if (effectiveOnline) {
                    if (isElectron()) {
                        dispatch(AppActions.setCloudAuthStatus({
                            status: 'invalid',
                            reason: 'auth_check_failed',
                            updatedAt: Date.now(),
                        }));
                    } else {
                        dispatch(AppActions.forceLogout());
                    }
                } else {
                    showOfflineDialog();
                }
            });
    }

    const checkAuthAndScan = async () => {
        // In Electron local/offline mode, do not require an online auth check.
        if (isElectron() && useLocalProcessing) {
            const scannerId = userProfile?.settings?.scanner_id;
            if (isDoubleSidedScanner(scannerId)) {
                await checkStatusAndStartDoubleSidedScan();
            } else {
                checkStatusAndStartScan();
            }
            return;
        }
        Authorization.currentUserInfo()
            .then(async () => {
                const scannerId = userProfile?.settings?.scanner_id;

                // Check if double-sided scanner is configured
                if (isDoubleSidedScanner(scannerId)) {
                    await checkStatusAndStartDoubleSidedScan();
                } else {
                    checkStatusAndStartScan();
                }
            })
            .catch((e) => {
                console.log('error occurred', e)
                if (effectiveOnline) {
                    if (isElectron()) {
                        dispatch(AppActions.setCloudAuthStatus({
                            status: 'invalid',
                            reason: 'auth_check_failed',
                            updatedAt: Date.now(),
                        }));
                    } else {
                        dispatch(AppActions.forceLogout());
                    }
                } else {
                    showOfflineDialog();
                }
            });
    }

    const startReadingIfAllowed = () => {
        // If the current draft is locked, auto-start a fresh reading (equivalent to "New Reading"),
        // so scan/upload remains usable without forcing the user to press New Reading explicitly.
        const jobIdToUse = draftLocked ? uuid.v4().toString() : (draftJobId || uuid.v4().toString());

        if (draftLocked) {
            dispatch(JobActions.newDraftReading(jobIdToUse));
        } else if (!draftJobId) {
            // Preserve any draftInfo the user may have typed before the draft id was initialized.
            dispatch(JobActions.setDraftJobId(jobIdToUse));
        }

        dispatch(JobActions.commitDraftInfoForJob({
            jobId: jobIdToUse,
            info: draftLocked ? {} : (draftInfo || {})
        }));

        // Lock the draft so the Info tab reflects the captured values (or empty values) for this reading.
        dispatch(JobActions.setDraftLocked(true));

        return jobIdToUse;
    };

    const startNewReadingPinned = () => {
        const newJobId = uuid.v4().toString();
        if (draftJobId) {
            dispatch(JobActions.clearCommittedDraftInfo(draftJobId));
        }
        dispatch(JobActions.newDraftReading(newJobId));
    };

    const resetDraftInfoPinned = () => {
        dispatch(JobActions.clearDraftInfo());
    };

    const processManualJob = async (payload: IManualJobEntryInput) => {
        const jobId = startReadingIfAllowed();
        if (!jobId) return;

        setWaiting(true)
        setTimeout(() => {
            setWaiting(false)
        }, MANUAL_UPLOAD_TIMEOUT)

        dispatch(AnalyseAction.initiateManualJob.request({
            jobId: jobId,
            manualJobEntryInput: payload,
            location: await getCurrentLocation()
        }));
    }

    const setResponseAndUploadFile = async (data: any, source = "image") => {
        if (data?.assets === undefined ||
            data?.assets === null ||
            data?.assets.length === 0) {
            return
        }

        const jobId = startReadingIfAllowed();
        if (!jobId) return;

        setWaiting(true)
        setTimeout(() => {
            setWaiting(false)
        }, UPLOAD_TIMEOUT)

        console.log('data', data)

        dispatch(AnalyseAction.initiateUpload.request({
            jobId: jobId,
            isBase64: false,
            dataObject: data?.assets[0],
            source: source,
            location: await getCurrentLocation(),
            ...(selectedImageData.jobOptions || {})
        }));
    }

    const setResponseAndUploadDoubleSidedFile = async (data: any, source = "image") => {
        if (data?.assets === undefined ||
            data?.assets === null ||
            data?.assets.length === 0
            || data?.assets.length === 1) {
            return
        }

        const jobId = startReadingIfAllowed();
        if (!jobId) return;

        setWaiting(true)
        setTimeout(() => {
            setWaiting(false)
        }, UPLOAD_TIMEOUT)
        dispatch(AnalyseAction.initiateDoubleSidedUpload.request({
            jobId: jobId,
            isBase64: false,
            dataObject: data?.assets,
            source: source,
            location: await getCurrentLocation()
        }));
    }

    const checkStatusAndStartScan = async () => {
        const jobId = startReadingIfAllowed();
        if (!jobId) return;

        setWaiting(true)
        setTimeout(() => {
            setWaiting(false)
        }, SCAN_TIMEOUT)

        dispatch(AnalyseAction.initiateScan.request({
            jobId: jobId,
            source: "image",
            location: await getCurrentLocation()
        }));
    }

    const checkStatusAndStartDoubleSidedScan = async () => {
        try {
            // Check scanner availability
            const scannerStatus = await windowsScan.detectDoubleSidedScanners();

            if (!scannerStatus.available) {
                const message = scannerStatus.count === 0
                    ? "No scanners detected. Please connect 2 USB scanners for double-sided scanning."
                    : `Double-sided scanning requires 2 USB scanners. Currently detected: ${scannerStatus.count}. Please connect a second scanner.`;

                // Show error dialog
                setDialogTitle('Scanner Error');
                setDialogConfirmText('OK');
                setDialogStatusMessage(message);
                setDialogAction(null);
                setDialogVisible(true);
                return;
            }

            const jobId = startReadingIfAllowed();
            if (!jobId) return;

            // Set waiting state with timeout (use longer timeout for double-sided scan)
            setWaiting(true);
            setTimeout(() => {
                setWaiting(false);
            }, DOUBLE_SIDE_SCAN_TIMEOUT);

            dispatch(AnalyseAction.initiateDoubleSidedScan.request({
                jobId: jobId,
                source: "image",
                location: await getCurrentLocation()
            }));
        } catch (error) {
            console.error('Error checking scanner status:', error);
            setDialogTitle('Scanner Error');
            setDialogConfirmText('OK');
            setDialogStatusMessage("Error checking scanner availability. Please try again.");
            setDialogAction(null);
            setDialogVisible(true);
        }
    }

    const checkStatusAndSelectDoubleSidedImages = async () => {
        try {
            // Open file picker to select 2 images
            const filePaths = await windowsScan.selectDoubleSidedImages();

            // User cancelled selection
            if (!filePaths) {
                return;
            }

            const jobId = startReadingIfAllowed();
            if (!jobId) return;

            // Set waiting state with timeout
            setWaiting(true);
            setTimeout(() => {
                setWaiting(false);
            }, UPLOAD_TIMEOUT);

            dispatch(AnalyseAction.initiateDoubleSidedUpload.request({
                jobId: jobId,
                isBase64: false, // File paths, not base64
                dataObject: filePaths, // Array of 2 file paths
                source: "image",
                location: await getCurrentLocation()
            }));
        } catch (error: any) {
            console.error('Error selecting images:', error);
            setDialogTitle('File Selection Error');
            setDialogConfirmText('OK');
            setDialogStatusMessage(error.message || "Error selecting images. Please try again.");
            setDialogAction(null);
            setDialogVisible(true);
        }
    }

    const displayScanError = (errorType: string) => {
        setDialogTitle('Scanner Error');
        setDialogConfirmText('Setup Scanner');
        setDialogAction(() => () => {
            navigation.push('ConnectToRpi', {
                returnBackTo: 'Home',
            });
        });

        if (errorType === JobStatus.WIFI_NOT_CONNECTED) {
            setDialogStatusMessage("Wifi is not connected on the InstaGrainX IoT device. Please connect it to a Wifi network and try again.");
        } else if (errorType === JobStatus.SCANNER_NOT_READY) {
            setDialogStatusMessage("Scanner is not ready. Please check if it is connected and powered on.");
        } else if (errorType === JobStatus.IOT_NOT_CONNECTED) {
            setDialogStatusMessage("InstaGrainX IoT device is not connected. Please connect it to your computer and try again.");
        } else if (errorType === JobStatus.JOB_IMAGE_SCAN_ERROR) {
            setDialogStatusMessage("Error occured while scanning image. Please check scanner and try again.");
        }

        setDialogVisible(true);
    }

    const getProgressBarMessage = () => {
        if (jobStatus === JobStatus.JOB_IMAGE_SCANNING) {
            return 'Scanning image...';
        } else if (
            jobStatus === JobStatus.JOB_IMAGE_UPLOADING) {
            return 'Uploading image...';
        } else if (jobStatus === JobStatus.JOB_CREATING_MANUAL_ENTRY) {
            return 'Processing...';
        }
        return "Processing...";
    }

    const isProgressBarVisible = () => {
        const waitingForResult = isImageScanningOrUploading(jobStatus);

        console.log('jobStatus', jobStatus, 'waitingForResult', waitingForResult, 'waiting', waiting)

        if (waitingForResult && waiting) {
            return true
        }
        return false
    }

    const imageUploadButtonClick = () => {
        if (isProgressBarVisible()) {
            console.log('Scan or upload in progress')
            return
        }

        console.log('Single sided image upload button clicked')
        launchImageLibrary({
            selectionLimit: 0,
            mediaType: 'photo',
            includeBase64: false,
        }, (data) => checkAuthAndUpload(data, "image"))
    }

    const doubleSidedImageUploadButtonClick = () => {
        if (isProgressBarVisible()) {
            console.log('Scan or upload in progress')
            return
        }
        console.log('Double sided image upload button clicked');
        const platformOs = Platform.OS as string;

        // Electron runs the web bundle, so Platform.OS is usually "web".
        // Use the Electron file picker so we get real filesystem paths (not blob: URLs),
        // which local processing expects.
        if (isElectron()) {
            checkStatusAndSelectDoubleSidedImages();
            return;
        }

        if (platformOs === 'windows'
            || platformOs === 'macos'
            || platformOs === 'linux') {
            checkStatusAndSelectDoubleSidedImages();
            return;
        }

        if (Platform.OS === 'web') {
            pickWebImages({ selectionLimit: 2, accept: 'image/*' })
                .then((result) => {
                    if (!result) return;
                    checkAuthAndDoubleSidedUpload(result, "image");
                })
                .catch((error) => {
                    console.error('Error selecting images:', error);
                    setDialogTitle('File Selection Error');
                    setDialogConfirmText('OK');
                    setDialogStatusMessage(error?.message || "Error selecting images. Please try again.");
                    setDialogAction(null);
                    setDialogVisible(true);
                });
            return;
        }

        launchImageLibrary({
            selectionLimit: 2,
            mediaType: 'photo',
            includeBase64: false,
        }, (data) => checkAuthAndDoubleSidedUpload(data, "image"))
    }

    const imageCaptureButtonClick = () => {
        if (isProgressBarVisible()) {
            console.log('Scan or upload in progress')
            return
        }
        if (lightboxEnabled) {
            launchCamera({
                mediaType: 'photo',
                includeBase64: false,
                saveToPhotos: true,
            }, (data) => checkAuthAndUpload(data, "camera", { productName: 'LIGHT_BOX' }))
            return
        }
        launchCamera({
            mediaType: 'photo',
            includeBase64: false,
            saveToPhotos: true,
        }, (data) => checkAuthAndUpload(data, "camera"))
    }

    const manualEntryButtonClicked = async (payload: IManualJobEntryInput) => {
        if (isProgressBarVisible()) {
            console.log('Scan or upload in progress')
            return
        }

        await checkAuthAndManualUpload(payload);
    }

    const uploadSelectedImage = () => {
        // Function to upload the selected image
        if (!selectedImageData
            || !selectedImageData.assets
            || selectedImageData.assets.length === 0
        ) {
            return
        }

        if (selectedImageData.doubleSided) {
            setResponseAndUploadDoubleSidedFile(selectedImageData, selectedImageData.source)
        } else {
            setResponseAndUploadFile(selectedImageData, selectedImageData.source)
        }
    }

    const renderImagePreview = () => {
        const previewAssets = selectedImageData?.assets ?? [];
        const isDoubleSidedPreview = selectedImageData?.doubleSided && previewAssets.length > 1;

        const isLandscape = width > height;
        const availableHeight = Math.max(120, height - 220);

        if (!isDoubleSidedPreview) {
            return (
                <Image
                    alt='Image Preview'
                    source={{ uri: previewAssets[0]?.uri }}
                    style={{
                        width: width,
                        height: availableHeight,
                        resizeMode: 'contain',
                    }} />
            );
        }

        const twoAssets = previewAssets.slice(0, 2);

        if (isLandscape) {
            const imageWidth = Math.max(120, Math.floor(width / 2) - 24);
            return (
                <HStack space={3} justifyContent="center">
                    {twoAssets.map((asset, idx) => (
                        <Box key={asset?.uri ?? `preview-${idx}`}>
                            <Text mb={2} fontSize="sm" fontWeight="600">
                                Side {idx + 1}
                            </Text>
                            <Image
                                alt={`Image Preview Side ${idx + 1}`}
                                source={{ uri: asset?.uri }}
                                style={{
                                    width: imageWidth,
                                    height: availableHeight,
                                    resizeMode: 'contain',
                                }} />
                        </Box>
                    ))}
                </HStack>
            );
        }

        const imageHeight = Math.max(120, Math.floor((availableHeight - 16) / 2));
        return (
            <VStack space={4}>
                {twoAssets.map((asset, idx) => (
                    <Box key={asset?.uri ?? `preview-${idx}`}>
                        <Text mb={2} fontSize="sm" fontWeight="600">
                            Side {idx + 1}
                        </Text>
                        <Image
                            alt={`Image Preview Side ${idx + 1}`}
                            source={{ uri: asset?.uri }}
                            style={{
                                width: width,
                                height: imageHeight,
                                resizeMode: 'contain',
                            }} />
                    </Box>
                ))}
            </VStack>
        );
    };

    return (
        <>
            <Modal
                size={"full"}
                isOpen={showPreview}>
                <Modal.Content>
                    <Modal.CloseButton />
                    <Modal.Header>Image Preview</Modal.Header>
                    <Modal.Body>
                        <Box>
                            <ScrollView>
                                {renderImagePreview()}
                            </ScrollView>
                        </Box>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button.Group space={2}>
                            <Button variant="ghost" colorScheme="blueGray" onPress={() => {
                                setSelectedImageData({
                                    assets: [],
                                    source: "",
                                    doubleSided: false,
                                })
                                setShowPreview(false);
                            }}>
                                Cancel
                            </Button>
                            <Button
                                onPress={async () => {
                                    setShowPreview(false);
                                    uploadSelectedImage();
                                }
                                }>Upload</Button>
                        </Button.Group>
                    </Modal.Footer>
                </Modal.Content>
            </Modal>
            <Box variant={"main"} style={[containerStyle.mainContainer, {
                height: '100%',
            }]}>
                <CustomAlertDialog
                    title={dialogTitle}
                    description={dialogStatusMessage}
                    cancelText='Cancel'
                    confirmText={dialogConfirmText}
                    isOpen={dialogVisible}
                    onCancelClicked={hideDialog}
                    onConfirmClicked={() => {
                        hideDialog();
                        if (dialogAction) {
                            dialogAction();
                        }
                    }}
                />
	                <InfoStatusBar navigation={navigation} />
	                <Box mx={3} mt={2} mb={1} px={3} py={2} rounded="8" bg={sampleIdBarBg}>
	                    <HStack alignItems="center" justifyContent="space-between" space={3}>
	                        <Box flex={1} mr={2}>
	                            <HStack alignItems="center" space={2}>
	                                <Text fontSize="xs" fontWeight="600" color={sampleIdBarText}>
	                                    {getText('sampleId')}
	                                </Text>
	                                {draftLocked ? (
	                                    <Text fontSize="xs" fontWeight="700" color="green.600">
	                                        Captured
	                                    </Text>
	                                ) : null}
	                            </HStack>
	                            <Text fontSize="sm" color={sampleIdBarText}>
	                                {draftJobId ? getDisplayableJobId(draftJobId) : '—'}
	                            </Text>
	                            {draftLocked ? (
	                                <Text mt={1} fontSize="xs" color={sampleIdBarText}>
	                                    This sample’s info is locked. Edit it from the Analysis page, or tap New Reading to start a new sample.
	                                </Text>
	                            ) : null}
	                        </Box>
	                        <HStack alignItems="center" space={2}>
	                            <Button
	                                size="sm"
	                                colorScheme="green"
	                                variant="solid"
	                                isDisabled={isProgressBarVisible()}
	                                onPress={startNewReadingPinned}
	                            >
	                                {getText('newReading')}
	                            </Button>
	                            <Button
	                                size="sm"
	                                variant="outline"
	                                colorScheme="coolGray"
	                                isDisabled={draftLocked}
	                                onPress={resetDraftInfoPinned}
	                            >
	                                {getText('resetFields')}
	                            </Button>
	                        </HStack>
	                    </HStack>
	                </Box>
	                <Box style={{
	                    marginTop: 20,
	                }}>
                    {
                        isProgressBarVisible() &&
	                        <ProgressBar
	                            isVisible={isProgressBarVisible()}
	                            withoutOverlay={true}
	                            progressText={getProgressBarMessage()}
	                            progressSubText={"Fill out job info before taking a reading. You can edit it later from the Analysis page."} />
	                    }
	                </Box>
	                <MeasurementInfo />
	                <FabButtons
	                    isDisabled={isProgressBarVisible()}
	                    connectScannerClicked={() => navigation.navigate('ConnectToRpi', { returnBackTo: 'Home' })}
	                    scanImageClicked={async () => await checkAuthAndScan()}
	                    imageUploadClicked={imageUploadButtonClick}
	                    captureImageClicked={imageCaptureButtonClick}
	                    doubleSidedImageUploadClicked={doubleSidedImageUploadButtonClick}
                    manualEntryClicked={async (payload: IManualJobEntryInput) => {
                        await manualEntryButtonClicked(payload)
                    }}
                />
            </Box>
        </>
    );
}
